import React, { createContext, useState, useContext, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import { secureApi, secureAuthApi, secureUserApi } from '../services/csrfApi';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ページ読み込み時にローカルストレージからトークンを取得
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('auth_token');
      
      if (token) {
        try {
          // トークンが有効かチェック（有効期限など）
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          
          if (decodedToken.exp < currentTime) {
            // トークンの有効期限切れ
            secureApi.clearTokens();
            setIsAuthenticated(false);
            setCurrentUser(null);
          } else {
            // Initialize security (CSRF token)
            await secureApi.initialize();
            
            // ユーザー情報を取得
            const response = await secureUserApi.getProfile();
            setCurrentUser(response.data);
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error('Authentication error:', error);
          secureApi.clearTokens();
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      }
      
      setIsLoading(false);
    };
    
    loadUser();
  }, []);

  // ログイン関数
  const login = async (email, password) => {
    try {
      setError(null);
      
      // Initialize security first
      await secureApi.initialize();
      
      // APIへログインリクエスト (CSRF protected)
      const response = await secureAuthApi.login({
        username: email,
        password: password
      });
      
      const { access_token } = response.data;
      
      // トークンをローカルストレージに保存
      localStorage.setItem('auth_token', access_token);
      
      // ユーザー情報を取得
      const userResponse = await secureUserApi.getProfile();
      setCurrentUser(userResponse.data);
      setIsAuthenticated(true);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.detail || 'ログインに失敗しました');
      return false;
    }
  };

  // ログアウト関数
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate server-side session
      await secureAuthApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear client-side state regardless of server response
      setCurrentUser(null);
      setIsAuthenticated(false);
    }
  };

  // コンテキストの値
  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};